import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Database, Loader2, BarChart3, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import ChartLegend, { useToggleableSeries } from "@/components/ChartLegend";
import OpenAIAdsConnectionPanel from "@/components/openai_ads/OpenAIAdsConnectionPanel";
import { Kpi, Th, Td, HierarchyCard } from "@/components/openai_ads/OpenAIAdsHelpers";

const TREND_COLORS = { impressions: "#10A37F", clicks: "#002FA7" };
// Stable references for inline-prop hot spots — keeps Recharts /
// HierarchyCard from re-rendering on every parent render because of
// new array / object identities.
const TREND_KEYS = ["impressions", "clicks"];
const TREND_COLORS_ARR = [TREND_COLORS.impressions, TREND_COLORS.clicks];
const TREND_LABELS = { impressions: "Impressions", clicks: "Clicks" };
const CHART_MARGIN = { top: 4, right: 12, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };
const DOT_R2 = { r: 2 };
const DOT_R4 = { r: 4 };
const CAMPAIGN_COLS = [
  { key: "name", label: "Name", mono: false },
  { key: "status", label: "Status" },
];
const AD_GROUP_COLS = CAMPAIGN_COLS;  // identical shape — reuse the same identity
const AD_COLS = [
  { key: "name", label: "Name", mono: false },
  { key: "review_status", label: "Review" },
  { key: "status", label: "Status" },
];

const fmtNum = (n) => Number.isFinite(Number(n))
  ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(n))
  : "—";
const fmtCurrency = (n, ccy = "USD") => Number.isFinite(Number(n))
  ? new Intl.NumberFormat("en-US", { style: "currency", currency: ccy || "USD",
                                     maximumFractionDigits: 2 }).format(Number(n))
  : "—";
const fmtPct = (n) => Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "—";

function todayYMD() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function daysAgoYMD(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function ymd(d) { return d.toISOString().slice(0, 10); }

// Preset → [start, end] in YYYY-MM-DD. `null` start = "all time" (we cap at
// 2020-01-01 since the API requires a concrete start_date).
const PRESETS = {
  this_month:    { label: "This month",     compute: () => {
    const d = new Date();
    return [ymd(new Date(d.getFullYear(), d.getMonth(), 1)), todayYMD()];
  } },
  last_month:    { label: "Last month",     compute: () => {
    const d = new Date();
    const s = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const e = new Date(d.getFullYear(), d.getMonth(), 0);
    return [ymd(s), ymd(e)];
  } },
  last_7:        { label: "Last 7 days",    compute: () => [daysAgoYMD(6),  todayYMD()] },
  last_30:       { label: "Last 30 days",   compute: () => [daysAgoYMD(29), todayYMD()] },
  last_90:       { label: "Last 3 months",  compute: () => [daysAgoYMD(89), todayYMD()] },
  last_180:      { label: "Last 6 months",  compute: () => [daysAgoYMD(179), todayYMD()] },
  last_365:      { label: "Last 12 months", compute: () => [daysAgoYMD(364), todayYMD()] },
  ytd:           { label: "Year-to-date",   compute: () => {
    const d = new Date();
    return [`${d.getFullYear()}-01-01`, todayYMD()];
  } },
  all:           { label: "All time",       compute: () => ["2020-01-01", todayYMD()] },
  custom:        { label: "Custom…",        compute: null },
};

export default function ClientOpenAIAdsPage() {
  const { clientId } = useParams();
  const [connection, setConnection] = useState({ connected: false });
  const [loading, setLoading] = useState(true);

  // Connection form state, sync state and disconnect logic now live in
  // <OpenAIAdsConnectionPanel/>.

  // Sync state — insights only (hierarchy sync is owned by the connection panel)
  const [insightsSyncing, setInsightsSyncing] = useState(false);

  // Dashboard
  const [aggLevel, setAggLevel] = useState("ad");
  const [datePreset, setDatePreset] = useState("last_30");
  const [startDate, setStartDate] = useState(daysAgoYMD(30));
  const [endDate, setEndDate] = useState(todayYMD());
  const [insights, setInsights] = useState({ rows: [], totals: {} });
  const [campaigns, setCampaigns] = useState([]);
  const [adGroups, setAdGroups] = useState([]);
  const [ads, setAds] = useState([]);
  const [runs, setRuns] = useState([]);

  // Series-toggle state for the trend chart (impressions / clicks).
  const trendToggle = useToggleableSeries();

  // Apply a preset to startDate/endDate (custom keeps the existing values).
  const applyPreset = (key) => {
    setDatePreset(key);
    const p = PRESETS[key];
    if (p && p.compute) {
      const [s, e] = p.compute();
      setStartDate(s); setEndDate(e);
    }
  };

  const refreshConnection = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/openai-ads/connection`);
      setConnection(data);
    } catch (e) {
      toast.error("Failed to load OpenAI Ads connection");
    } finally { setLoading(false); }
  };

  const refreshDashboard = async () => {
    try {
      const [c, g, a, r] = await Promise.all([
        api.get(`/clients/${clientId}/openai-ads/campaigns`).then((x) => x.data).catch(() => []),
        api.get(`/clients/${clientId}/openai-ads/ad-groups`).then((x) => x.data).catch(() => []),
        api.get(`/clients/${clientId}/openai-ads/ads`).then((x) => x.data).catch(() => []),
        api.get(`/clients/${clientId}/openai-ads/sync-runs`).then((x) => x.data).catch(() => []),
      ]);
      setCampaigns(c || []); setAdGroups(g || []); setAds(a || []); setRuns(r || []);
    } catch (e) { /* dashboard refresh failure is non-blocking */ }
  };

  const refreshInsights = async () => {
    try {
      const { data } = await api.get(
        `/clients/${clientId}/openai-ads/insights`,
        { params: { aggregation_level: aggLevel, start_date: startDate, end_date: endDate } },
      );
      setInsights(data || { rows: [], totals: {} });
    } catch (e) { /* insights load failure is non-blocking */ }
  };

  useEffect(() => {
    refreshConnection();
    refreshDashboard();
  }, [clientId]);

  useEffect(() => {
    if (connection.connected) refreshInsights();
  }, [connection.connected, aggLevel, startDate, endDate]);

  // Connection / hierarchy handlers moved to <OpenAIAdsConnectionPanel/>.

  const syncInsights = async () => {
    setInsightsSyncing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/openai-ads/sync/insights`, {
        start_date: startDate, end_date: endDate,
        aggregation_level: aggLevel,
        time_granularity: "daily",
        write_to_platform_data: true,
      });
      toast.success(`Imported ${data.insights_written} insight rows · ${data.platform_data_rows} into platform_data`);
      await Promise.all([refreshConnection(), refreshDashboard(), refreshInsights()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Insights sync failed");
    } finally { setInsightsSyncing(false); }
  };

  const kpiTotals = useMemo(() => {
    const t = insights.totals || {};
    return {
      impressions: Number(t.impressions || 0),
      clicks: Number(t.clicks || 0),
      spend: Number(t.spend || 0),
      ctr: Number(insights.ctr || 0),
      cpc: Number(insights.cpc || 0),
      cpm: Number(insights.cpm || 0),
    };
  }, [insights]);

  // Build a daily-aggregated trend series across ALL entities so the chart
  // shows whether traffic is climbing or falling regardless of pivot level.
  const trendSeries = useMemo(() => {
    const byDay = new Map();
    (insights.rows || []).forEach((r) => {
      const day = r.readable_time || (r.start_time ? r.start_time.slice(0, 10) : null);
      if (!day) return;
      let row = byDay.get(day);
      if (!row) { row = { day, impressions: 0, clicks: 0, spend: 0 }; byDay.set(day, row); }
      row.impressions += Number(r.impressions || 0);
      row.clicks += Number(r.clicks || 0);
      row.spend += Number(r.spend || 0);
    });
    return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
  }, [insights]);

  // Simple direction signal — compare the last third of the window's average
  // to the first third's. Helps surface a single "is it growing?" verdict.
  const trendDirection = useMemo(() => {
    if (trendSeries.length < 4) return null;
    const cut = Math.max(1, Math.floor(trendSeries.length / 3));
    const first = trendSeries.slice(0, cut);
    const last = trendSeries.slice(-cut);
    const avg = (arr, k) => arr.reduce((s, r) => s + r[k], 0) / Math.max(arr.length, 1);
    const out = {};
    ["impressions", "clicks"].forEach((k) => {
      const a = avg(first, k); const b = avg(last, k);
      out[k] = a > 0 ? ((b - a) / a) * 100 : null;
    });
    return out;
  }, [trendSeries]);

  if (loading) {
    return <div className="p-8 text-sm text-zinc-500" data-testid="openai-ads-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="openai-ads-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#10A37F] text-white">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <div className="label-overline">CHATGPT / OPENAI ADS</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">OpenAI Ads</h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Connect this client&apos;s OpenAI Ads account and import campaign, ad-group, ad
            and daily insights data straight into your reports and charts.
          </p>
        </div>
      </div>

      {/* === Connection panel (encapsulated) === */}
      <OpenAIAdsConnectionPanel
        clientId={clientId}
        connection={connection}
        onRefresh={refreshConnection}
        onAfterSync={async () => { await Promise.all([refreshConnection(), refreshDashboard()]); }}
      />

      {/* Everything below only when connected */}
      {connection.connected && (
        <>
          {/* === Dashboard controls + KPIs === */}
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-zinc-500" />
                  <div className="label-overline">Performance</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={aggLevel} onChange={(e) => setAggLevel(e.target.value)}
                    className="h-9 rounded-sm border border-zinc-200 bg-white px-2 text-xs font-mono"
                    data-testid="openai-ads-agg-level">
                    <option value="ad">By ad</option>
                    <option value="ad_group">By ad group</option>
                    <option value="campaign">By campaign</option>
                    <option value="ad_account">Account total</option>
                  </select>
                  <select value={datePreset} onChange={(e) => applyPreset(e.target.value)}
                    className="h-9 rounded-sm border border-zinc-200 bg-white px-2 text-xs font-mono w-[150px]"
                    data-testid="openai-ads-date-preset">
                    {Object.entries(PRESETS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  {datePreset === "custom" && (
                    <>
                      <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                        className="rounded-sm h-9 w-[150px] font-mono text-xs"
                        data-testid="openai-ads-start-date" />
                      <span className="text-xs text-zinc-400">→</span>
                      <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                        className="rounded-sm h-9 w-[150px] font-mono text-xs"
                        data-testid="openai-ads-end-date" />
                    </>
                  )}
                  {datePreset !== "custom" && (
                    <span className="font-mono text-[10px] text-zinc-500"
                      data-testid="openai-ads-date-display">
                      {startDate} → {endDate}
                    </span>
                  )}
                  <Button size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                    disabled={insightsSyncing} onClick={syncInsights}
                    data-testid="openai-ads-sync-insights">
                    {insightsSyncing
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      : <Database className="h-3.5 w-3.5 mr-1.5" />}
                    {insightsSyncing ? "Importing…" : "Import insights"}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Kpi label="Impressions" value={fmtNum(kpiTotals.impressions)} testId="kpi-imp" />
                <Kpi label="Clicks" value={fmtNum(kpiTotals.clicks)} testId="kpi-clk" />
                <Kpi label="Spend" value={fmtCurrency(kpiTotals.spend, connection.currency_code)} testId="kpi-spend" />
                <Kpi label="CTR" value={fmtPct(kpiTotals.ctr)} testId="kpi-ctr" />
                <Kpi label="CPC" value={fmtCurrency(kpiTotals.cpc, connection.currency_code)} testId="kpi-cpc" />
                <Kpi label="CPM" value={fmtCurrency(kpiTotals.cpm, connection.currency_code)} testId="kpi-cpm" />
              </div>

              {/* Traffic trend chart — impressions + clicks across all campaigns */}
              {trendSeries.length > 0 && (
                <div className="rounded-sm border border-zinc-200 bg-white p-4"
                  data-testid="openai-ads-trend-chart">
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                      <div className="label-overline">Traffic trend</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Daily total impressions + clicks across every campaign.
                      </div>
                    </div>
                    {trendDirection && (
                      <div className="flex gap-3 text-[11px] font-mono">
                        {["impressions", "clicks"].map((k) => {
                          const d = trendDirection[k];
                          if (d === null || !Number.isFinite(d)) return null;
                          const up = d >= 0;
                          return (
                            <div key={k} className="inline-flex items-center gap-1"
                              data-testid={`trend-${k}`}>
                              <span className="uppercase tracking-wider text-zinc-500">{k}</span>
                              <span className={up ? "text-emerald-600" : "text-red-600"}>
                                {up ? "▲" : "▼"} {Math.abs(d).toFixed(1)}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <ChartLegend
                    keys={TREND_KEYS}
                    colors={TREND_COLORS_ARR}
                    labels={TREND_LABELS}
                    hidden={trendToggle.hidden}
                    onToggle={trendToggle.toggle}
                    testIdPrefix="openai-ads-trend-legend"
                  />
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={trendSeries} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="day" stroke="#71717A" fontSize={11} />
                      <YAxis yAxisId="left" stroke="#71717A" fontSize={11}
                        tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <YAxis yAxisId="right" orientation="right" stroke="#71717A" fontSize={11}
                        tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      {!trendToggle.isHidden("impressions") && (
                        <Line yAxisId="left" type="monotone" dataKey="impressions"
                          name="Impressions" stroke={TREND_COLORS.impressions} strokeWidth={2}
                          dot={DOT_R2} activeDot={DOT_R4} />
                      )}
                      {!trendToggle.isHidden("clicks") && (
                        <Line yAxisId="right" type="monotone" dataKey="clicks"
                          name="Clicks" stroke={TREND_COLORS.clicks} strokeWidth={2}
                          dot={DOT_R2} activeDot={DOT_R4} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {insights.rows.length > 0 && (
                <div className="rounded-sm border border-zinc-200 overflow-hidden">
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-600">
                    {insights.rows.length} rows · {aggLevel}
                  </div>
                  <div className="overflow-x-auto max-h-[480px]">
                    <table className="w-full text-xs">
                      <thead className="bg-white border-b border-zinc-100 sticky top-0">
                        <tr>
                          <Th>Date</Th>
                          {aggLevel !== "ad_account" && <Th>Entity</Th>}
                          <Th right>Impr.</Th>
                          <Th right>Clicks</Th>
                          <Th right>Spend</Th>
                          <Th right>CTR</Th>
                          <Th right>CPC</Th>
                          <Th right>CPM</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {insights.rows.slice(0, 200).map((r, i) => (
                          <tr key={`${r.openai_insight_id || r.openai_ad_id || i}-${r.start_time || i}`}
                            className="border-b border-zinc-50 hover:bg-zinc-50/60">
                            <Td mono>{r.readable_time || r.start_time?.slice(0, 10) || "—"}</Td>
                            {aggLevel !== "ad_account" && (
                              <Td className="max-w-[320px] truncate" title={r.ad_name || r.ad_group_name || r.campaign_name}>
                                <div className="truncate">
                                  {aggLevel === "ad" && (r.ad_name || r.openai_ad_id)}
                                  {aggLevel === "ad_group" && (r.ad_group_name || r.openai_ad_group_id)}
                                  {aggLevel === "campaign" && (r.campaign_name || r.openai_campaign_id)}
                                </div>
                                <div className="font-mono text-[10px] text-zinc-400 truncate">
                                  {aggLevel === "ad" && r.openai_ad_id}
                                  {aggLevel === "ad_group" && r.openai_ad_group_id}
                                  {aggLevel === "campaign" && r.openai_campaign_id}
                                </div>
                              </Td>
                            )}
                            <Td right mono>{fmtNum(r.impressions)}</Td>
                            <Td right mono>{fmtNum(r.clicks)}</Td>
                            <Td right mono>{fmtCurrency(r.spend, connection.currency_code)}</Td>
                            <Td right mono>{fmtPct(r.ctr)}</Td>
                            <Td right mono>{fmtCurrency(r.cpc, connection.currency_code)}</Td>
                            <Td right mono>{fmtCurrency(r.cpm, connection.currency_code)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* === Hierarchy tables === */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <HierarchyCard
              title="Campaigns"
              rows={campaigns}
              cols={CAMPAIGN_COLS}
              testId="openai-ads-campaigns"
              emptyMsg="No campaigns synced yet. Click 'Sync now'." />
            <HierarchyCard
              title="Ad groups"
              rows={adGroups}
              cols={AD_GROUP_COLS}
              testId="openai-ads-ad-groups"
              emptyMsg="No ad groups synced yet." />
            <HierarchyCard
              title="Ads"
              rows={ads}
              cols={AD_COLS}
              testId="openai-ads-ads"
              emptyMsg="No ads synced yet." />
          </div>

          {/* === Sync run history === */}
          {runs.length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-zinc-500" />
                  <div className="label-overline">Recent sync runs</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50 border-b border-zinc-100">
                      <tr>
                        <Th>Started</Th><Th>Type</Th><Th>Status</Th>
                        <Th right>Rows read</Th><Th right>Rows written</Th><Th>Error</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((r) => (
                        <tr key={r.run_id} className="border-b border-zinc-50"
                          data-testid={`openai-ads-run-${r.run_id}`}>
                          <Td mono>{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</Td>
                          <Td>{r.sync_type}</Td>
                          <Td>
                            <Badge className={`rounded-sm ${r.status === "success"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-red-50 text-red-700 border-red-200"}`}>
                              {r.status}
                            </Badge>
                          </Td>
                          <Td right mono>{r.rows_read ?? "—"}</Td>
                          <Td right mono>{r.rows_written ?? "—"}</Td>
                          <Td mono className="text-red-700 max-w-[320px] truncate">
                            {r.error_message || "—"}
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// Meta, Kpi, Th, Td, HierarchyCard now live in
// /components/openai_ads/OpenAIAdsHelpers.js — see import above.

